import {createSlice, PayloadAction} from "@reduxjs/toolkit";
import {ContactState, initialContactState} from "../state/ContactState";
import {createContactLookupMap} from "../util/Formatters";
import {DbContact, DbDavContact} from "../db/DbSchema";
import {applySyncChanges, applySyncChangesWithClientIds} from "../reducers/SyncHelper";
import {AddRemoteOperationPayload, SyncActions} from "./SyncSlice";
import {ItemChangeListDto} from "../services/messages/ItemChangeListDto";
import {EnrichedDavContact, VCardContactData} from "../services/messages/VCardContactData";
import {getDavMultiPropertyValue, getDavPropertyValue} from "../util/DavUtil";

// See here:
// https://redux-toolkit.js.org/api/createSlice#extrareducers

export const contactsSlice = createSlice({
    name: "contacts",
    initialState: initialContactState,
    reducers: {
        contactsLoaded: handleContactsLoaded,
        davContactsLoaded: handleDavContactsLoaded,
        setContactSearchText(state, {payload}: PayloadAction<string>) {
            state.contactSearchText = payload;
        },
    },
    extraReducers: builder =>
        builder
            .addCase(SyncActions.syncResponseReceived, handleSyncResponse)
     .addCase(SyncActions.addRemoteOperation, handleAddRemoteOperation)
});

function enrichDavContact(davContact: VCardContactData): EnrichedDavContact {
    return {
        ...davContact,
        Category: getDavPropertyValue(davContact, "CATEGORIES"),
        DisplayName: getDavPropertyValue(davContact, "FN") || getDavMultiPropertyValue(davContact.props["N"]?.[0])?.join() || "<unnamed contact>",
        Emails: davContact.props["EMAIL"]?.map(p => p.value ?? "") ?? [],
        PhoneNumbers: davContact.props["TEL"]?.map(p => p.value ?? "") ?? [],
    };
}

function handleContactsLoaded(state: ContactState, {payload: contacts}: PayloadAction<DbContact[]>) {
    state.contacts = contacts;
}

function handleDavContactsLoaded(state: ContactState, {payload: davContacts}: PayloadAction<DbDavContact[]>) {
    state.davContacts = davContacts.map(c => enrichDavContact(c));
    state.contactLookupMap = createContactLookupMap(state.davContacts);
}

function handleSyncResponse(state: ContactState, {payload: changes}: PayloadAction<ItemChangeListDto>) {
    if (changes.Contacts) {
        state.contacts = applySyncChangesWithClientIds(state.contacts, changes.Contacts, c => c);
    }
    if (changes.DavContacts) {
        state.davContacts = applySyncChanges(state.davContacts, changes.DavContacts, c => enrichDavContact(c));
        // TODO: create the contact lookup map
        state.contactLookupMap = createContactLookupMap(state.davContacts);
    }
}

function handleAddRemoteOperation(state: ContactState, {payload}: PayloadAction<AddRemoteOperationPayload>) {
    // TODO: allowing editing contacts and add two-way sync
//     const newContacts = applyClientDtoRemoteOperationLocally(payload.operation.ManageContact, state.contacts, c => c);
//     if (newContacts !== state.contacts) {
//         state.contactLookupMap = createContactLookupMap(newContacts);
//         state.contacts = newContacts;
//     }
}

export namespace ContactActions {
    export const {
        contactsLoaded,
        davContactsLoaded,
        setContactSearchText,
    } = contactsSlice.actions;
}
